<template>
    <div v-if="routine" class="lib-row" :class="{ readonly }" :style="!!$listeners.click ? 'cursor: pointer' : null"
        @click.stop="onClick" @mouseover="$emit('hover')" @mouseout="$emit('hover-out')"
        @mousedown="$emit('mousedown', routine, $event)">
        <div v-if="showArrows" class="lib-arrow" @click.stop="$emit('click:prev')">
            <v-icon size="20">mdi-chevron-left</v-icon>
        </div>
        <div v-if="routineImg" class="lib-thumb" :style="{ backgroundImage: `url('${routineImg}')` }" />
        <div v-else class="lib-icon">
            <v-icon size="20" color="accent">mdi-dumbbell</v-icon>
        </div>
        <div class="lib-info">
            <div class="lib-name text-truncate">{{ routine.name || routine.versus }}</div>
            <div class="lib-meta text-truncate">
                <template v-if="isCompetence">{{ $t('common.competence') }}</template>
                <template v-else>
                    {{ routineBlocksQuantity }}
                    <span class="text-lowercase">{{ $tc(`common.blocks`, 2) }}</span>
                </template>
                <template v-if="!noDurationText"> &middot; {{ routineDuration }}</template>
            </div>
            <div v-if="studentId || studentName" class="lib-meta text-truncate">
                {{ $t('routines.assigned_to', { name: studentNameString }) }}
            </div>
        </div>
        <div v-if="badge" class="lib-badge">{{ badge }}</div>
        <v-menu offset-y v-if="withMenu">
            <template v-slot:activator="{ on, attrs }">
                <button type="button" class="lib-more" v-bind="attrs" v-on="on" @click.stop @mousedown.stop>
                    <v-icon size="18">mdi-dots-vertical</v-icon>
                </button>
            </template>
            <v-list dense>
                <v-list-item link @click="assignRoutine" v-if="!noAssign">
                    <v-icon size="18" color="txtSecondary" class="mr-2">mdi-account-plus-outline</v-icon>
                    <v-list-item-title>{{ $t('routines.assign_routine') }}</v-list-item-title>
                </v-list-item>
                <v-divider v-if="!noAssign" />
                <v-list-item link @click="editRoutine">
                    <v-icon size="18" color="txtSecondary" class="mr-2">mdi-pencil-outline</v-icon>
                    <v-list-item-title>{{ $t('routines.edit_routine') }}</v-list-item-title>
                </v-list-item>
                <v-list-item link @click="copyRoutine">
                    <v-icon size="18" color="txtSecondary" class="mr-2">mdi-content-copy</v-icon>
                    <v-list-item-title>{{ $t('routines.copy_routine') }}</v-list-item-title>
                </v-list-item>
                <v-list-item link @click="saveInFolder" v-if="!noFolder">
                    <v-icon size="18" color="txtSecondary" class="mr-2">mdi-folder-outline</v-icon>
                    <v-list-item-title>{{ $t('common.save_in_folder') }}</v-list-item-title>
                </v-list-item>
                <v-list-item link @click="shareRoutine" v-if="canShare">
                    <v-icon size="18" color="txtSecondary" class="mr-2">mdi-share-variant</v-icon>
                    <v-list-item-title>{{ $t('routines.share_routine') }}</v-list-item-title>
                </v-list-item>
                <v-divider />
                <v-list-item link @click="deleteRoutine">
                    <v-icon size="18" color="error" class="mr-2">mdi-trash-can-outline</v-icon>
                    <v-list-item-title class="error--text">{{ $t('routines.delete_routine') }}</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>
        <div v-if="showArrows" class="lib-arrow" @click.stop="$emit('click:next')">
            <v-icon size="20">mdi-chevron-right</v-icon>
        </div>

        <kimsa-confirm ref="confirmCopy" :title="$t('common.duplicate')" :content="$t('routines.confirm_duplicate')"
            :persistent="false" :loading="loadingCopy" @confirm="doCopyRoutine" :close-on-confirm="false" />
    </div>
</template>

<script>

import { db, Timestamp } from '@/utils/Firebase'
import KimsaConfirm from '@/components/common/KimsaConfirm'

export default {
    components: { KimsaConfirm },
    props: {
        routine: {
            type: Object,
            default: null,
        },
        studentId: {
            type: String,
            default: null,
        },
        withMenu: {
            type: Boolean,
            default: false,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        noFolder: {
            type: Boolean,
            default: false,
        },
        studentName: {
            type: String,
            default: null,
        },
        showArrows: {
            type: Boolean,
            default: false
        },
        noDurationText: {
            type: Boolean,
            default: false
        },
        noAssign: {
            type: Boolean,
            default: false
        },
        emitCopy: {
            type: Boolean,
            default: false
        },
        emitEditAssigned: {
            type: Boolean,
            default: false
        },
        canShare: {
            type: Boolean,
            default: false
        },
        badge: {
            type: [Number, String],
            default: null,
        },
    },
    data() {
        return {
            student: null,
            minutes: 0,
            loadingCopy: false,
        }
    },
    async mounted() {
        const vm = this
        await vm.getStudentData()
        vm.getRoutineMinutes()
    },
    computed: {
        isCompetence(vm = this) {
            return vm.routine?.type === 'competence' || false
        },
        routineImg(vm = this) {
            return vm.student?.avatar || vm.routine.img || null
        },
        routineDuration(vm = this) {
            if (!vm.minutes) return vm.$t('common.not_available_short')
            let minutesString = vm.$tc(`common.minutes`, Number(vm.minutes) === 1 ? 1 : 2).toLowerCase()
            let minutesAdded = Number(vm.minutes * 1.3).toFixed()
            if (Number(vm.minutes) === Number(minutesAdded)) return `${vm.minutes} ${minutesString}`
            return `${vm.minutes} - ${minutesAdded} ${minutesString}`
        },
        studentNameString(vm = this) {
            return vm.studentName || vm.student?.name || '...'
        },
        routineBlocksQuantity(vm = this) {
            return vm.routine?.blocksQuantity || vm.routine?.blocks?.length || 0
        },
        isLgScreen(vm = this) {
            return vm.$vuetify.breakpoint.lgAndUp
        },
        contentCols(vm = this) {
            if (vm.showArrows) return vm.isLgScreen ? 8 : 7
            return 9
        },
    },
    methods: {
        onClick() {
            const vm = this
            vm.$emit('click')
        },
        editRoutine() {
            const vm = this
            if (vm.routine?.rawDate) {
                if (vm.routine?.type === 'competence') return vm.$emit('click:edit:competence')
                if (vm.emitEditAssigned) return vm.$emit('click:edit:assigned')
                return vm.pushRoute({ name: `${vm.accountType}.routines.edit.assigned`, params: { uid: vm.routine.id } })
            }
            vm.$emit('click:edit')
        },
        assignRoutine() {
            const vm = this
            vm.$emit('click:assign')
        },
        copyRoutine() {
            const vm = this
            if (vm.emitCopy) return vm.$emit('click:copy')
            vm.$refs.confirmCopy.show()
        },
        async doCopyRoutine() {
            let vm = this
            await vm.withLoading(async () => {
                let routine = vm.routine

                if (!routine.blocks) {
                    let ref = await db.collection('routines').doc(routine.objectID).get()
                    routine = ref.data()
                }

                routine.owner = 'user'
                routine.coachId = vm.currentUser.uid
                routine.name = `${routine.name} ${vm.$t('common.copy_template')}`
                delete routine.created_at
                delete routine.objectID

                routine = JSON.parse(JSON.stringify(routine))

                let newRoutine = await db.collection('routines').add({ ...routine, used: 0, created_at: Timestamp.now() })

                vm.toast(vm.$t('routines.copied'))
                vm.$refs.confirmCopy?.hide()

                if (vm.$listeners['click:duplicated'])
                    return vm.$emit('click:duplicated', { ...routine, objectID: newRoutine.id })

                vm.pushRoute({ name: 'coach.routines.edit', params: { uid: newRoutine.id } })
            }, 'loadingCopy')
        },
        deleteRoutine() {
            const vm = this
            vm.$emit('click:delete')
        },
        saveInFolder() {
            const vm = this
            vm.$emit('click:folder')
        },
        shareRoutine() {
            const vm = this
            vm.$emit('click:share')
        },
        async getStudentData() {
            const vm = this
            if (!vm.studentId) return;
            let studentRef = await db.collection('users').doc(vm.studentId).get()
            vm.student = studentRef.data()
        },
        getRoutineMinutes() {
            const vm = this
            let minutes = 0
            if (vm.routine.minutes) return vm.minutes = vm.routine.minutes
            vm.routine?.blocks?.forEach(block => {
                block.exercises.forEach(exercise => {
                    minutes += exercise?.config?.sets?.length || block.sets || 0
                })
            })
            vm.minutes = minutes
        }
    },
    watch: {
        studentId() {
            this.getStudentData()
        },
        routine() {
            this.getRoutineMinutes()
        }
    }
}
</script>

<style scoped>
.lib-row {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    padding: 12px 14px;
    display: flex;
    align-items: center;
    gap: 12px;
    transition: all .15s;
}

.lib-row:hover {
    border-color: var(--v-outline2-base);
    background: var(--v-surface2-base);
}

.lib-row.readonly {
    pointer-events: none;
}

.lib-thumb {
    width: 42px;
    height: 42px;
    min-width: 42px;
    border-radius: 8px;
    background-size: cover;
    background-position: center;
    background-color: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    flex-shrink: 0;
}

.lib-icon {
    width: 42px;
    height: 42px;
    min-width: 42px;
    border-radius: 8px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.lib-info {
    flex: 1;
    min-width: 0;
}

.lib-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.lib-meta {
    font-size: 11px;
    color: var(--v-txtSecondary-base);
    margin-top: 2px;
}

.lib-more {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--v-txtMuted-base);
    flex-shrink: 0;
    transition: all .15s;
}

.lib-more:hover {
    color: var(--v-txtSecondary-base);
    border-color: var(--v-outline2-base);
}

.lib-more .v-icon {
    color: inherit !important;
}

.lib-badge {
    width: 20px;
    height: 20px;
    min-width: 20px;
    border-radius: 50%;
    background: var(--v-primary-base);
    color: #0C0E12;
    font-size: 11px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.lib-arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--v-txtMuted-base);
    flex-shrink: 0;
}

.lib-arrow:hover {
    color: var(--v-txtPrimary-base);
}
</style>